import Image from "next/image";

import {
  ProductsByCategoriesQuery,
  setStandaloneProductModal,
  useProductsByVariationIdsQuery,
} from "@chef/state-management";
import {
  getDiscountedAmount,
  getPriceOfProductByVariation,
  getProductFeaturedImage,
} from "@chef/state-management/helpers";
import { Card, CurrencyWithDiscount } from "@chef/components";
import { BASE_64_1X1_GREY, language } from "@chef/constants";
import { useAppDispatch } from "@chef/state-management/hooks";

import { isEmptyArray } from "@chef/utils/array";

interface ISpecificGroceries {
  _type: "specificGroceries";
  groceries: string[];
  week: number;
  year: number;
}

interface GroceriesComponentProps {
  value?: ISpecificGroceries;
}

type ProductsByCategories =
  ProductsByCategoriesQuery["productsByCategories"][number]["products"][number];

interface GroceriesCardProps {
  product: ProductsByCategories["variations"][number];
}

const GroceriesCard = ({ product }: GroceriesCardProps) => {
  const imageUrl = getProductFeaturedImage(product);

  const discount = getDiscountedAmount(product);
  const price = getPriceOfProductByVariation(product);

  const onTriggerProductModal = () => {
    dispatch(
      setStandaloneProductModal({
        open: true,
        productByCategoryVariation: product,
      }),
    );
  };

  const dispatch = useAppDispatch();

  return (
    <Card className="relative h-full" noPadding>
      <div className="relative flex h-full md:flex-col">
        <button
          type="button"
          onClick={onTriggerProductModal}
          className="relative w-2/5 aspect-square md:aspect-2/3 md:w-full"
        >
          <Image
            alt={product.name}
            src={imageUrl || BASE_64_1X1_GREY}
            fill
            className="object-cover rounded-tl md:object-fill md:rounded-none md:rounded-t"
            sizes="(min-width: 768px) 25vw, 20vw"
          />
        </button>

        <div
          className="flex flex-col justify-between flex-grow w-full h-full p-2 md:p-3"
          id={product.variationId}
        >
          <button
            type="button"
            onClick={onTriggerProductModal}
            className="text-left line-clamp-2"
          >
            {product.name}
          </button>

          <div className="flex items-end justify-between w-full mt-2">
            <CurrencyWithDiscount
              language={language}
              className="mt-2"
              discount={discount}
              total={price}
              emphasized
            />
          </div>
        </div>
      </div>
    </Card>
  );
};

export const GroceriesComponent = ({ value }: GroceriesComponentProps) => {
  const week = value?.week || 0;
  const year = value?.year || 0;

  const variationIds = value?.groceries || [];

  const skip = !week || !year || isEmptyArray(variationIds);

  const { data: productsByVariationIds } = useProductsByVariationIdsQuery(
    {
      variationIds,
      week: week!,
      year: year!,
    },
    {
      skip,
    },
  );

  const preselectedGroceryProducts = productsByVariationIds?.products;
  if (!preselectedGroceryProducts || !value) {
    return null;
  }

  return (
    <div className="grid items-stretch gap-2 my-4 md:gap-x-4 md:gap-y-14 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
      {preselectedGroceryProducts.map(({ variations }) => {
        return variations.map((variation) => {
          return (
            <GroceriesCard key={variation.variationId} product={variation} />
          );
        });
      })}
    </div>
  );
};
